import { AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, QueryList, ViewChildren } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { fadeInLeftAnimation } from 'src/@omnial/_animations/fade-in-left.animation';
import { fadeInRightAnimation } from 'src/@omnial/_animations/fade-in-right.animation';
import { fadeInUpAnimation } from 'src/@omnial/_animations/fade-in-up.animation';
import { scaleInAnimation } from 'src/@omnial/_animations/scale-in.animation';
import { ProductDetail, ProductSlide } from 'src/@omnial/_models/catalog/product.model';
import { ScriptLoaderService } from 'src/@omnial/_services/external/script-load.service';
import { ProductZoomComponent } from 'src/@omnial/pages/product/product-zoom/product-zoom.component';
import { SwiperOptions } from 'swiper/types';
import { ThumbsOptions } from 'swiper/types';
import { AppSettings } from 'src/app/app.settings';
import { YouTubePlayer } from '@angular/youtube-player';

@Component({
    selector: 'app-product-swiper',
    templateUrl: './../../../../app/pages/product/product-swiper/product-swiper.component.html',
    styleUrls: ['./../../../../app/pages/product/product-swiper/product-swiper.component.scss'],
    animations: [fadeInUpAnimation, fadeInRightAnimation, fadeInLeftAnimation, scaleInAnimation],
    standalone: false
})

export class ProductSwiperComponent implements AfterViewInit, AfterViewChecked {
  @Input() effect: "slide" | "fade" | "cube" | "coverflow" | "flip" = 'slide';
  @Input() zoomButton: boolean = true;
  @Input() product: ProductDetail;
  @Input() startingSlide: number;

  @ViewChildren('productSwiper') productSwiper: QueryList<ElementRef>;
  @ViewChildren('productThumbsSwiper') productThumbsSwiper: QueryList<ElementRef>;
  @ViewChildren('youTubePlayer') youTubePlayer: QueryList<YouTubePlayer>;

  public isGallery = false;
  public image = '';
  public config: SwiperOptions = null;
  public thumbsConfig: SwiperOptions = {
    spaceBetween: 1,
    slidesPerView: 4,
    loop: false,
    freeMode: true,
    watchSlidesProgress: true
  };
  public thumbsOptions: ThumbsOptions = null;
  public slides: ProductSlide[] = [];
  public subscriptions: Subscription[] = [];
  public showZoom: boolean = false;

  constructor(public dialog: MatDialog,
    public settings: AppSettings,
    public scriptLoaderService: ScriptLoaderService,
    public cd: ChangeDetectorRef) { }

  ngAfterViewChecked(): void {
  }

  ngAfterViewInit(): void {
  }

  public initSwiper(product?: ProductDetail) {
    this.config = null;
    this.slides = [];
    if (product) {
      this.product = product;
    }

    // For nopCommerce products we add a product attribute for a Video ref in YouTube with the default value as the Youtube ref
    const slides: ProductSlide[] = [];
    var videoAttributes = this.product.attributes?.filter(a => a.textPrompt.toLowerCase() === 'youtube');
    if (videoAttributes && videoAttributes.length > 0) {
      this.product.youTubeVideoAttributes = [];
      videoAttributes.forEach(va => {
        if (va.defaultValue) {
          this.product.youTubeVideoAttributes.push(va.defaultValue);
        }
      })
    }
    if (this.product.youTubeVideoAttributes?.length > 0) {
      this.product.youTubeVideoAttributes.forEach((video) => {
        const newSlide = new ProductSlide();
        newSlide.youTubeRef = video;
        newSlide.alt = this.product.name;
        newSlide.index = 0;
        newSlide.imageIndex = -1;
        newSlide.isVideo = true;
        slides.push(newSlide);
      });
    }
    if (this.product.images?.length > 0) {
      let index = 0;
      this.product.images.forEach((image) => {
        const newSlide = new ProductSlide();
        newSlide.imgUrl = image.large;
        newSlide.alt = this.product.name;
        newSlide.index = index;
        newSlide.imageIndex = index;
        slides.push(newSlide);
        index++;
      })
    }
    setTimeout(() => {
      this.slides = slides;
      if (this.product.youTubeVideoAttributes) {
        this.scriptLoaderService.load('Youtube');
        this.subscriptions.push(this.scriptLoaderService.scripts.subscribe(
          res => {
            if (res && res.find(s => s.ref === 'Youtube')?.loaded) {
              this.swiperConfig();
            }
          }));
      } else {
        this.swiperConfig();
      }
    });
  }

  public swiperConfig(): void {
    let initialSlide = 0;
    if (this.startingSlide && this.startingSlide < this.slides.length) {
      initialSlide = this.startingSlide;
    }
    this.config = {
      initialSlide: initialSlide,
      effect: this.effect,
      cubeEffect: {
        slideShadows: false,
        shadow: false
      },
      resizeObserver: true,
      slidesPerView: 1,
      spaceBetween: 0,
      on: {
        slideChange: () => { this.onSlideChange() }
      }
    };
    this.productThumbsSwiper?.forEach((swiper) => {
      Object.assign(swiper.nativeElement, this.thumbsConfig);
      swiper.nativeElement.initialize();
      this.config.thumbs = swiper.nativeElement;
    });
    this.productSwiper?.forEach((swiper) => {
      Object.assign(swiper.nativeElement, this.config);
      swiper.nativeElement.initialize();
      this.zoomDisplay();
      setTimeout(() => { this.thumbActive() }, 500);
    });
  }

  public zoomDisplay(): void {
    if (!this.zoomButton) {
      this.showZoom = false;
    }
    let index = this.getCurrentSlideIndex();
    this.showZoom = !this.slides[index]?.youTubeRef;
    this.cd.detectChanges();
  }

  public thumbActive(): void {
    if (this.slides.length < 1) {
      return;
    }
    let index = this.getCurrentSlideIndex();
    for (var i = 0; i < this.slides.length; i++) {
      const thumbImage = document.getElementsByClassName('pdp-swipe-thumb-' + i);
      if (thumbImage) {
        for (var j = 0; j < thumbImage.length; j++) {
          if (i == index) {
            thumbImage[j].classList.add('swiper-thumb-active');
          } else {
            thumbImage[j].classList.remove('swiper-thumb-active');
          }
        }
      }
    }
    this.showZoom = !this.slides[index]?.youTubeRef;
    this.cd.detectChanges();
  }

  public slideNext() {
    this.productSwiper.forEach((swiper) => {
      swiper.nativeElement?.swiper?.slideNext(500);
    })
  }
  public slidePrev() {
    this.productSwiper.forEach((swiper) => {
      swiper.nativeElement?.swiper?.slidePrev(500);
    })
  }

  public onSlideChange() {
    this.zoomDisplay();
    this.thumbActive();
    this.youTubePlayer?.forEach((player) => {
      if (player?.getCurrentTime()) {
        player?.pauseVideo();
      }
    });
  }

  public openZoom(): void {
    let index = this.getCurrentSlideIndex();
    let imageIndex = index;
    if (this.product.youTubeVideoAttributes && this.product.youTubeVideoAttributes.length > 0) {
      imageIndex -= this.product.youTubeVideoAttributes.length;
    }
    if (index !== -1 && this.product.images[imageIndex]) {
      this.dialog.open(ProductZoomComponent, {
        data: this.product.images[imageIndex],
        panelClass: 'product-zoom',
        direction: (this.settings.rtl) ? 'rtl' : 'ltr'
      });
    }
  }

  public getCurrentSlideIndex(): number {
    let index = -1;
    this.productSwiper?.forEach((swiper) => {
      index = swiper?.nativeElement?.swiper?.activeIndex ?? -1;
    });
    return index;
  }
}
